import React from "react"
import { format } from 'date-fns'
import { getDayOfWeek } from '../helpers/getWeekday'
import { getPaymentMethod } from "../helpers/getPaymentMethod"
import { formatVariant } from "../helpers/formatVariant"
import styles from './PrintCateringOrder.module.css'
import { Order } from "../types/Order"
import { getFormattedPrice } from "../helpers/formatPrice"
import Comment from '../icons/Comment'
import { getPaymentStatusText } from '../helpers/getPaymentStatusText'

function PrintCateringOrder({ order }: {order: Order}) {
  const {
    userName,
    phoneNumber,
    email,
    street,
    city,
    postNumber,
    orderItemsCatering,
    restaurantMessage,
    deliveryType,
    deliveryDisplayName,
    deliveryTimeSlot,
    orderNo,
    paymentStatus,
    paymentProviderType,
    totalPrice,
    deliveryFee,
    totalDiscount,
    storeComment
  } = order || {}

  const totalNumberOfArticles = React.useMemo(() => {
    return order.orderItemsCatering?.reduce((acc, item) => acc + item.amount, 0)
  }, [order])

  const deliveryDate = format(new Date(order?.deliveryDate), 'yyyy-MM-dd')
  const dayOfWeek = React.useMemo(() => {
    return getDayOfWeek(deliveryDate)
  }, [deliveryDate])
  const paymentMethod = React.useMemo(() => {
    return getPaymentMethod(paymentProviderType)
  }, [paymentProviderType])

  function getPaymentStatus(){
    return getPaymentStatusText(paymentStatus, deliveryDate)
  }

  return order ? (
    <div className={styles.printContainer}>
      <div className={styles.header}>
        {deliveryDate && (
          <span className={styles.deliveryDate}>
            {dayOfWeek}, {deliveryDate} {deliveryTimeSlot ? `, ${deliveryTimeSlot}` : ''}
          </span>
        )}
        <h2 className={styles.orderNumber}>
          {orderNo.toUpperCase()}
        </h2>
      </div>

      {/* LEVERANS, KUNDUPPGIFTER, BETALNING */}
      <div className={styles.orderInfoContainer}>
        <p className={styles.orderInfoHeading}>Betalning</p>
        <div className={styles.orderInfoContent}>
          <span className={styles.boldText}>
            Betalsätt: {paymentMethod === 'Betala i butik' ? <span>Ej betald<span className={styles.normalText}>, kunden ska betala i butik i samband med upphämtning</span></span> : paymentMethod}
          </span>
          {paymentMethod !== 'Betala i butik' && 
          <span>
            Betalstatus: {getPaymentStatus()}
          </span>
          }
        </div>
      </div>
      <div className={styles.orderInfoContainer}>
        <p className={styles.orderInfoHeading}>Leverans</p>
        <div className={styles.orderInfoContent}>
          {deliveryDisplayName && <span>Leveranssätt: {deliveryDisplayName}</span>}
          {deliveryDate && (
            <span>
              {deliveryTimeSlot ? 'Leveransdag och tid: ' : 'Leveransdag: '}
              {dayOfWeek}, {deliveryDate}{deliveryTimeSlot ? `, ${deliveryTimeSlot}` : ''}
            </span>
          )}
          {deliveryType === 1000 && street && postNumber && city && (
            <p>
              {`Leveransadress: ${street}, ${postNumber} ${city}`}
            </p>
          )}
          {storeComment && (
            <span>
              Intern butikskommentar: {storeComment}
            </span>
          )}
        </div>
      </div>
      <div className={styles.orderInfoContainer}>
        <p className={styles.orderInfoHeading}>Kunduppgifter</p>
        <div className={styles.orderInfoContent}> 
          {userName && <span>Namn: {userName}</span>}
          {phoneNumber && <span>Telefonnummer: {phoneNumber}</span>}
          {email && <span>Mejl: {email}</span>}
        </div>
      </div>

      {restaurantMessage && (
        <div className={styles.orderInfoContainer}>
          <p className={styles.orderInfoHeading}>Kundkommentar</p>
          <div className={styles.orderComment}>
            <Comment className={styles.commentIcon} color='var(--text)'/>
            {restaurantMessage}
          </div>
        </div>
      )}

      {/* BESTÄLLNING */}
      <div className={styles.orderItemsContainer}>
        <h2 className={styles.orderItemsHeading}>Beställning</h2>
        {orderItemsCatering &&
          orderItemsCatering.length > 0 &&
          orderItemsCatering.map(({ id, amount, name, priceGross, variantName, message }) => {
            if (amount > 0) {
              return (
                <div 
                  key={id}
                  className={`${styles.orderItem} ${styles.noBreak}`}
                >
                  <div className={styles.orderItemHeader}>
                    <span className={styles.itemName}>
                      {name}
                    </span>
                    {variantName &&
                    formatVariant(variantName)?.map((variant: string, index: number) => (
                      <span key={index}>
                        {variant} 
                      </span>
                    ))}
                  </div>
                  <div className={styles.orderItemDetails}> 
                    <div className={styles.priceRow}> 
                      <div className={styles.boldText}>
                        {amount} st
                      </div>
                      <div>
                        <span className={styles.itemPrice}>{getFormattedPrice(priceGross)}/st</span>
                        <span className={styles.boldText}>
                          {getFormattedPrice(Math.round(priceGross * amount * 100) * 0.01)}
                        </span>
                      </div>
                    </div>  
                      
                    {message && (
                      <div className={styles.itemComment}>
                        <Comment className={styles.commentIcon} color='var(--text)'/>
                        <span>
                          Meddelande från kund: <span className={styles.boldText}>{message}</span>
                        </span>
                      </div>
                    )}
                      
                  </div>
                </div>
              )
            }
          })}
      </div>
           
      {/* TOTALT */}
      <div className={styles.totalContainer}>
        {totalDiscount > 0 ? (
          <div className={styles.discountRow}>
            <span className={styles.boldText}>Rabatt</span>
            <span className={styles.boldHeading}>-{totalDiscount} kr</span>
          </div>
        ) : null}
        <div className={styles.totalRow}>
          <div> 
            <span className={styles.totalLabel}>
              Totalt
              <span className={styles.lightText}> ({totalNumberOfArticles} st artiklar)</span>
            </span>
          </div>
          <div className={styles.totalPriceContainer}>
            <span className={styles.boldHeading}>{totalPrice} kr</span>
            <span className={styles.deliveryFee}>{deliveryFee > 0 ? `varav leveranskostnad ${deliveryFee} kr` : ''}</span>
          </div>
        </div>
      </div>
    </div>
  ) : null
}
export default PrintCateringOrder
